import { router } from 'expo-router';
import { Linking } from 'react-native';

import { trackEvent } from '@/src/lib/analytics';

export type TextLinkMatch = {
  displayText: string;
  end: number;
  start: number;
  url: string;
};

const LINK_PATTERN = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;
const TRAILING_PUNCTUATION_PATTERN = /[),.!?;:]+$/;
const INTERNAL_HOSTS = new Set([
  'frienzzi.com',
  'www.frienzzi.com',
  'frienzzi.web.app',
]);

function trimTrailingPunctuation(value: string) {
  let trimmed = value;

  while (trimmed.length > 0 && TRAILING_PUNCTUATION_PATTERN.test(trimmed)) {
    trimmed = trimmed.slice(0, -1);
  }

  return trimmed;
}

export function normalizeTextLink(rawValue: string) {
  const trimmed = trimTrailingPunctuation(rawValue.trim());
  const candidate = trimmed.startsWith('www.') ? `https://${trimmed}` : trimmed;

  try {
    const url = new URL(candidate);

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }

    url.password = '';
    url.username = '';
    return url.toString();
  } catch {
    return null;
  }
}

export function extractTextLinks(body: string): TextLinkMatch[] {
  const matches: TextLinkMatch[] = [];

  for (const match of body.matchAll(LINK_PATTERN)) {
    const rawMatch = match[0];
    const normalizedUrl = normalizeTextLink(rawMatch);

    if (!normalizedUrl) {
      continue;
    }

    const displayText = trimTrailingPunctuation(rawMatch);
    const start = match.index ?? 0;
    const end = start + displayText.length;

    matches.push({
      displayText,
      end,
      start,
      url: normalizedUrl,
    });
  }

  return matches;
}

function internalPathForUrl(url: URL) {
  if (!INTERNAL_HOSTS.has(url.hostname.toLowerCase())) {
    return null;
  }

  return `${url.pathname}${url.search}${url.hash}` || '/';
}

export async function openTextLink({
  source,
  url,
}: {
  source: string;
  url: string;
}) {
  let parsedUrl: URL;

  try {
    parsedUrl = new URL(url);
  } catch {
    return;
  }

  const internalPath = internalPathForUrl(parsedUrl);

  trackEvent('text_link_open', {
    host: parsedUrl.hostname.toLowerCase().slice(0, 120),
    internal: Boolean(internalPath),
    source,
  });

  if (internalPath) {
    router.push(internalPath as never);
    return;
  }

  await Linking.openURL(parsedUrl.toString());
}
